import { Ban, X } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { getJSON } from '@/lib/api'
import type { PipelineAction, PipelineRow } from '@/pages/RenewalPipelinePage.types'
import { displayMonthDay } from '@/lib/dates'

interface TimelineItem {
  date: string
  detail: string
  tentative: boolean
}

interface TimelineResponse {
  renewal_pipeline_timeline: {
    membership_id: number
    person_id: number
    name: string
    level: string
    fee: string
    renewal_due_on: string | null
    items: TimelineItem[]
  }
}

export function RenewalPipelineTimelineDialog({
  row,
  open,
  dismissing,
  onClose,
  onDismiss,
  onAction,
}: {
  row: PipelineRow | null
  open: boolean
  dismissing: boolean
  onClose: () => void
  onDismiss: (row: PipelineRow) => void
  onAction: (action: PipelineAction) => void
}) {
  const timelineQuery = useQuery({
    queryKey: ['renewal-pipeline', 'timeline', row?.membership_id],
    queryFn: () =>
      getJSON<TimelineResponse>(
        `/api/v1/renewal_pipeline/memberships/${row?.membership_id}/timeline`,
      ),
    enabled: row !== null,
    retry: false,
  })
  const timeline = timelineQuery.data?.renewal_pipeline_timeline

  return (
    <Dialog open={open && row !== null} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{timeline?.name ?? row?.name}</DialogTitle>
          <DialogDescription>{timelineDescription(timeline)}</DialogDescription>
        </DialogHeader>
        <TimelineDialogBody
          loading={timelineQuery.isLoading}
          failed={timelineQuery.isError}
          timeline={timeline}
        />
        <TimelineActions
          row={row}
          visible={timeline !== undefined}
          dismissing={dismissing}
          onDismiss={onDismiss}
          onAction={onAction}
        />
      </DialogContent>
    </Dialog>
  )
}

function TimelineDialogBody({
  loading,
  failed,
  timeline,
}: {
  loading: boolean
  failed: boolean
  timeline?: TimelineResponse['renewal_pipeline_timeline']
}) {
  if (loading) return <EmptyState title="Loading timeline" className="min-h-52" />
  if (failed) return <Alert>The member timeline could not be loaded.</Alert>
  return timeline ? <Timeline items={timeline.items} /> : null
}

function TimelineActions({
  row,
  visible,
  dismissing,
  onDismiss,
  onAction,
}: {
  row: PipelineRow | null
  visible: boolean
  dismissing: boolean
  onDismiss: (row: PipelineRow) => void
  onAction: (action: PipelineAction) => void
}) {
  if (!row || !visible) return null

  return (
    <DialogFooter>
      {row.dismissible && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="text-muted-foreground sm:mr-auto"
          disabled={dismissing}
          onClick={() => onDismiss(row)}
        >
          <X className="mr-1.5 size-3.5" aria-hidden="true" />
          {dismissing ? 'Dismissing…' : 'Dismiss'}
        </Button>
      )}
      {row.contact_kind !== 'organization' && !row.hold_ends_on && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onAction({ kind: 'hold', row })}
        >
          Hold…
        </Button>
      )}
      {row.contact_kind !== 'organization' && row.suspend_available && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="text-destructive hover:text-destructive"
          onClick={() => onAction({ kind: 'suspend', row })}
        >
          <Ban className="mr-1.5 size-3.5" aria-hidden="true" />
          Suspend
        </Button>
      )}
      {row.record_payment_available && (
        <Button type="button" size="sm" onClick={() => onAction({ kind: 'record-payment', row })}>
          Record payment
        </Button>
      )}
    </DialogFooter>
  )
}

function timelineDescription(timeline?: TimelineResponse['renewal_pipeline_timeline']) {
  if (!timeline) return 'Loading Membership timeline'
  return `${timeline.level} · ${timeline.fee} · due ${formatDate(timeline.renewal_due_on)}`
}

function Timeline({ items }: { items: TimelineItem[] }) {
  return (
    <ol className="py-1">
      {items.map((item, index) => (
        <li
          key={`${item.date}-${item.detail}`}
          className={`relative grid grid-cols-[5rem_1rem_1fr] gap-2 pb-5 last:pb-0 ${
            item.tentative ? 'text-muted-foreground' : 'text-foreground'
          }`}
        >
          <span className="pt-0.5 text-caption tabular-nums text-muted-foreground">
            {formatDate(item.date)}
          </span>
          <span className="relative flex justify-center pt-1.5" aria-hidden="true">
            {index < items.length - 1 && (
              <span className="absolute bottom-[-1.25rem] top-2 w-px bg-border" />
            )}
            <span
              className={`relative size-2 rounded-full ${
                item.tentative ? 'border border-muted-foreground bg-popover' : 'bg-foreground'
              }`}
            />
          </span>
          <span className="text-body-sm">{item.detail}</span>
        </li>
      ))}
    </ol>
  )
}

function formatDate(value: string | null) {
  return value ? displayMonthDay(value) : '—'
}
